import React, { useEffect } from 'react';

import { useCopyToClipboard } from '@uidotdev/usehooks';

import { sortULIDs } from '../../lib/stringUtils';

import { messageRowId } from '../chat/domIds';
import Announcement from './Announcement';
import ConferenceTranscript from './ConferenceTranscript';
import DayDivider from './DayDivider';
import EndOfMessages from './EndOfMessages';
import Message from './Message';
import WidgetAnnouncement from './WidgetAnnouncement';

const DebugOverlay = ({ message, isGroupedWithPrevious }) => {
  const [, copyToClipboard] = useCopyToClipboard();

  return (
    <div
      className={`absolute right-4 z-(--stack-debug) flex max-w-full gap-2 overflow-clip select-none ${isGroupedWithPrevious || message?.type === 'announcement' ? 'top-0' : 'top-4'}`}
    >
      {['id', 'sort_order', 'optimistic_sort_order', 'pseudotype'].map((key) => {
        if (message?.[key]) {
          return (
            <div
              key={key}
              title={key}
              className="cursor-pointer font-mono text-3xs text-gray-500"
              onClick={() => copyToClipboard(message[key])}
            >
              {message[key].length > 10 ? `–${message[key].slice(-10)}` : message[key]}
            </div>
          );
        }
      })}
    </div>
  );
};

const MessageRouter = ({ item, context, isGroupedWithPrevious, isGroupedWithNext, shouldShowUnreadMarker, onUnreadMarkerDismiss }) => {
  switch (item?.type) {
    case 'summary_artifact':
      return context.isDebugEnabled ? (
        <div data-id={item.id} data-sort-order={item.sort_order} className="rounded-lg bg-amber-200 px-3 py-2 text-3xs text-amber-800">
          <span className="font-blazeit">Archived breakout summary</span>
          <pre className="overflow-x-auto font-mono text-3xs text-amber-600">{JSON.stringify(item, null, 2)}</pre>
        </div>
      ) : null;
    case 'announcement':
      return <Announcement announcement={item} />;
    case 'conference_transcript':
      return <ConferenceTranscript conference_transcript={item} />;
    case 'widget_announcement':
      return <WidgetAnnouncement announcement={item} />;
    case 'end_of_messages':
      return (
        <EndOfMessages
          eom={item}
          currentChannel={context.currentChannel}
          currentChannelBreadcrumbs={context.currentChannelBreadcrumbs}
          isSharedChannel={context.isSharedChannel}
        />
      );
    default:
      return (
        <Message
          message={item}
          author={item.author}
          debugEnabled={context.isDebugEnabled}
          interactive={context.isInteractive}
          isGroupedWithPrevious={isGroupedWithPrevious}
          isGroupedWithNext={isGroupedWithNext}
          shouldShowUnreadMarker={shouldShowUnreadMarker}
          onUnreadMarkerDismiss={onUnreadMarkerDismiss}
          unreadMarkerWorkspaceId={context.currentChannel?.workspace_id}
          unreadMarkerProfileId={context.profileId}
          onOpenFullMessage={context.handleOpenFullMessageModal}
          onOpenImageLightbox={context.handleOpenImageLightbox}
        />
      );
  }
};

const DebugCommitProbe = ({ itemKey, onCommit }) => {
  useEffect(() => {
    onCommit(itemKey);
  });

  return null;
};

const MessageListItem = ({ data: item, context, nextData, prevData, areDifferentDays, debugItemKey, onDebugCommit }) => {
  if (!item) return null;

  const isGroupedWithPrevious = item.groupedWithPrev === true;
  const isGroupedWithNext = nextData?.groupedWithPrev === true;

  const shouldShowDateSeparator = prevData && item.timestamp && prevData.timestamp && areDifferentDays(prevData.timestamp, item.timestamp);

  // is this the first unread message? (the marker represents the last read message, so we show badge on the next message)
  const markerSortOrder = context.unreadDividerSortOrder;
  const isFirstUnreadMessage =
    item.sort_order &&
    markerSortOrder &&
    // current message is after the marker (unread)
    sortULIDs(markerSortOrder, item.sort_order) < 0 &&
    // previous message (if it exists and has a sort_order) must be before the marker (read)
    // if there's no previous message or it doesn't have a sort_order, this is the first message so show badge
    (!prevData?.sort_order || sortULIDs(markerSortOrder, prevData.sort_order) >= 0);

  // ..and should we actually show that marker right now
  const shouldShowUnreadMarker = isFirstUnreadMessage;

  // The multi-attachment scroller consumes these row-scoped variables so each
  // row bleeds to its own content-panel edges. The 70px start offset mirrors
  // Message.jsx's ml-12 avatar gutter plus this row's pl-[22px] content inset
  // (which lines bubble text up with the composer's input text), and the 1rem
  // rail bleed values mirror this row's px-4 panel padding.
  return (
    <div
      className="@container px-4 [--message-column-gutter:max(0px,calc((100cqi-42rem)/2))] [--message-content-start-offset:70px] [--message-rail-bleed-left:1rem] [--message-rail-bleed-right:1rem]"
      id={messageRowId(context.currentChannel?.id, item.sort_order)}
    >
      {debugItemKey && onDebugCommit ? <DebugCommitProbe itemKey={debugItemKey} onCommit={onDebugCommit} /> : null}
      <div className="mx-auto w-full max-w-2xl pl-[22px]">
        <DayDivider timestamp={item.timestamp} enabled={shouldShowDateSeparator} />
        <div className="relative">
          {context.isDebugEnabled && <DebugOverlay message={item} isGroupedWithPrevious={isGroupedWithPrevious} />}
          <MessageRouter
            item={item}
            context={context}
            isGroupedWithPrevious={isGroupedWithPrevious}
            isGroupedWithNext={isGroupedWithNext}
            shouldShowUnreadMarker={shouldShowUnreadMarker}
            onUnreadMarkerDismiss={context.clearUnreadDivider}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessageListItem);
